import React from "react";
import { motion } from "framer-motion";
import { Icon as Iconify } from "@iconify/react";
import useAnimationSettings from "../../hooks/useAnimationSettings";

const VIOLETA = "#697B68";
const ROSA = "#E09767";

const skills = [
  { label: "React", icon: "simple-icons:react" },
  { label: "Next.js", icon: "simple-icons:nextdotjs" },
  { label: "React Native", icon: "simple-icons:react" },
  { label: "Expo", icon: "simple-icons:expo" },
  { label: "Node.js", icon: "simple-icons:nodedotjs" },
  { label: "SQL", icon: "material-symbols:database-outline" },
  { label: "Firebase", icon: "simple-icons:firebase" },
  { label: "Arquitectura", icon: "material-symbols:account-tree-outline-rounded" },
  { label: "Tailwind", icon: "simple-icons:tailwindcss" },
  { label: "Sass", icon: "simple-icons:sass" },
  { label: "Git", icon: "simple-icons:git" },
  { label: "n8n", icon: "simple-icons:n8n" },
  { label: "OpenAI", icon: "simple-icons:openai" },
  { label: "Gemini", icon: "material-symbols:neurology" },
  { label: "Codex", icon: "material-symbols:code-blocks-outline-rounded" },
  { label: "Claude Code", icon: "simple-icons:anthropic" },
  { label: "Hostinger", icon: "simple-icons:hostinger" },
  { label: "Postman", icon: "simple-icons:postman" },
  { label: "Express", icon: "simple-icons:express" },
  { label: "LLM Workflows", icon: "material-symbols:auto-awesome" },
];

export default function Skills() {
  const { reduceMotion, allowHoverAnimations, durations, offsets, viewport } =
    useAnimationSettings();

  const containerVariants = reduceMotion
    ? {
        hidden: { opacity: 1 },
        show: { opacity: 1 },
      }
    : {
        hidden: { opacity: 0 },
        show: {
          opacity: 1,
          transition: {
            staggerChildren: durations.stagger,
            delayChildren: durations.stagger * 2,
          },
        },
      };

  const itemVariants = reduceMotion
    ? {
        hidden: { opacity: 1, y: 0, scale: 1 },
        show: { opacity: 1, y: 0, scale: 1 },
      }
    : {
        hidden: { opacity: 0, y: offsets.subtleY, scale: 0.98 },
        show: {
          opacity: 1,
          y: 0,
          scale: 1,
          transition: { duration: durations.reveal * 0.5 },
        },
      };

  return (
    <section
      className="
        w-full py-[var(--section-space)]
        bg-[#EADFD2]
      "
    >
      <div className="page-container">
        <div className="mx-auto mb-8 grid justify-items-center text-center md:mb-10">
          <h3
            className="section-title tracking-tight"
            style={{ color: VIOLETA }}
          >
            Tecnologías
          </h3>
          <div
            className="mt-3 h-1 w-24 rounded-full"
            style={{
              background: `linear-gradient(135deg, ${ROSA}, ${VIOLETA})`,
            }}
          />
          <p
            className="body-copy mt-5 hidden max-w-xl sm:block"
            style={{ color: `${VIOLETA}B3` }}
          >
            Tecnologías para desarrollo full stack, aplicaciones mobile, bases de datos e inteligencia artificial.
          </p>
        </div>

        <div
          className="
            skills-panel relative mx-auto max-w-5xl rounded-3xl border border-white/40 bg-white/70
            p-3 shadow-md backdrop-blur-md sm:p-8 sm:shadow-xl
          "
          style={{ boxShadow: "0 20px 45px -20px rgba(105,123,104,0.35)" }}
        >
          <div
            className="pointer-events-none absolute -inset-0.5 -z-10 rounded-[1.7rem] opacity-40 blur-2xl"
            style={{
              background: `linear-gradient(135deg, ${ROSA}, ${VIOLETA})`,
            }}
          />

          <motion.ul
            variants={containerVariants}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            className="skills-grid"
          >
            {skills.map(({ label, icon }) => (
              <motion.li
                key={label}
                variants={itemVariants}
                className={`
                  group motion-gpu relative flex flex-col items-center rounded-2xl
                  border border-[#697B681A] bg-white/80 p-3 sm:p-4
                  shadow-[0_6px_18px_-10px_rgba(105,123,104,0.25)]
                  transition-all duration-300
                  ${
                    allowHoverAnimations
                      ? "hover:-translate-y-1 hover:shadow-[0_16px_30px_-14px_rgba(105,123,104,0.35)]"
                      : ""
                  }
                `}
                whileHover={allowHoverAnimations ? { y: -4 } : undefined}
                whileTap={allowHoverAnimations ? { scale: 0.98 } : undefined}
                title={label}
              >
                <div className="h-11 w-11 shrink-0">
                  <Iconify
                    icon={icon}
                    width="2.75rem"
                    height="2.75rem"
                    style={{ color: VIOLETA }}
                    className={`transition-transform duration-300 ${
                      allowHoverAnimations ? "group-hover:scale-105" : ""
                    }`}
                  />
                </div>

                <p
                  className="small-copy mt-3 text-center font-semibold"
                  style={{ color: VIOLETA }}
                >
                  {label}
                </p>

                <span
                  className={`absolute bottom-2 left-1/2 h-[2px] w-0 -translate-x-1/2 rounded-full transition-all duration-300 ${
                    allowHoverAnimations ? "group-hover:w-10" : ""
                  }`}
                  style={{
                    background: `linear-gradient(90deg, ${ROSA}, ${VIOLETA})`,
                  }}
                />
                <span
                  className={`pointer-events-none absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-300 ${
                    allowHoverAnimations ? "group-hover:opacity-100" : ""
                  }`}
                  style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.6)" }}
                />
              </motion.li>
            ))}
          </motion.ul>
        </div>

        <div className="mt-6 hidden text-center sm:block sm:mt-8">
          <p className="text-xs" style={{ color: `${VIOLETA}80` }}>
            + Arquitectura, APIs, performance, automatización y workflows con IA para aplicaciones web y mobile.
          </p>
        </div>
      </div>
    </section>
  );
}
